/**
 * lib/families.ts
 * ------------------------------------------------------------------
 * Family helpers — the one place that keeps the family ↔ student link
 * consistent. `Student.familyId` is authoritative; `Family.studentIds`
 * is its denormalised mirror. Every membership change here updates both
 * sides in a single atomic batch, and moves a student cleanly out of a
 * previous family when reassigned.
 *
 * Pure selectors (familyMembers, etc.) are safe to use anywhere; the
 * write functions are used by the Families admin screen.
 * ------------------------------------------------------------------
 */
import { writeBatch, doc, deleteField } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { stripUndefined } from '@/lib/sanitize';
import type { Family, FamilyBillingPlan, FamilyPrimaryContact, Student } from '@/types';

const now = () => new Date().toISOString();

/** Local-only id (Firestore-friendly, mirrors the studentDocuments style). */
export function newFamilyId(): string {
  return `fam_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** The Student records that belong to a family. */
export function familyMembers(family: Family, students: Student[]): Student[] {
  const set = new Set(family.studentIds);
  return students.filter((s) => set.has(s.id));
}

/** Students not yet attached to any family. */
export function unlinkedStudents(students: Student[]): Student[] {
  return students.filter((s) => !s.familyId);
}

export interface FamilyInput {
  /** Omit to create; provide to update. */
  id?: string;
  name: string;
  primaryContact: FamilyPrimaryContact;
  billingAddress?: string;
  notes?: string;
  billingPlan?: FamilyBillingPlan;
  memberIds: string[];
}

export interface Actor {
  uid?: string;
  name?: string;
}

/**
 * Create or update a family and reconcile membership atomically:
 *   - stamps familyId on every member,
 *   - detaches students removed from this family,
 *   - removes moved students from their previous family's studentIds.
 * @returns the family id.
 */
export async function saveFamily(
  input: FamilyInput,
  ctx: { families: Family[]; students: Student[]; actor?: Actor },
): Promise<string> {
  const { families, students, actor } = ctx;
  const id = input.id || newFamilyId();
  const ts = now();
  const batch = writeBatch(db);

  const memberIds = [...new Set(input.memberIds)];
  const memberSet = new Set(memberIds);
  const existing = families.find((f) => f.id === id);
  const prevMembers = existing?.studentIds ?? [];

  // 1) students leaving THIS family → clear their familyId
  for (const sid of prevMembers) {
    if (!memberSet.has(sid)) {
      batch.update(doc(db, COLLECTIONS.students, sid), {
        familyId: deleteField(),
        updatedAt: ts,
      });
    }
  }

  // 2) students joining (or staying in) THIS family → stamp familyId,
  //    and note any that are moving away from a different family.
  //
  //    FULL-LOCKSTEP CONTACT PROPAGATION: the family's primary contact
  //    is the source of truth for its members. On every save we push the
  //    contact's email/WhatsApp onto each member's PRIMARY guardian (what
  //    the parent invite + portal read) and onto parentContact (what
  //    report-email delivery reads), so a family-level edit reaches both.
  //    Only non-empty values are pushed, so a blank family contact never
  //    wipes a child's existing details.
  const pc = input.primaryContact;
  const pcEmail = pc?.email?.trim() || undefined;
  const pcWhats = pc?.whatsapp?.trim() || undefined;
  const pcName = pc?.name?.trim() || undefined;
  const hasContact = !!(pcEmail || pcWhats || pcName);
  const buildMemberPatch = (stu?: Student): Record<string, unknown> => {
    const patch: Record<string, unknown> = { familyId: id, updatedAt: ts };
    if (!stu || !hasContact) return patch;
    // store #3 — report-delivery contact
    patch.parentContact = {
      guardianName: pcName ?? stu.parentContact?.guardianName,
      email: pcEmail ?? stu.parentContact?.email,
      whatsapp: pcWhats ?? stu.parentContact?.whatsapp,
      consentToSend: pc?.consentToSend ?? stu.parentContact?.consentToSend,
    };
    // store #2 — primary guardian's channels (parent invite / portal)
    const guardians = stu.admissions?.guardians;
    if (guardians && guardians.length) {
      let idx = guardians.findIndex((g) => g.isPrimaryContact);
      if (idx < 0 && guardians.length === 1) idx = 0;
      if (idx >= 0) {
        patch.admissions = {
          ...stu.admissions,
          guardians: guardians.map((g, i) =>
            i === idx
              ? { ...g, email: pcEmail ?? g.email, whatsapp: pcWhats ?? g.whatsapp }
              : g,
          ),
        };
      }
    }
    return stripUndefined(patch);
  };

  const otherFamiliesTouched = new Map<string, Set<string>>();
  for (const sid of memberIds) {
    const stu = students.find((s) => s.id === sid);
    const wasIn = stu?.familyId;
    if (wasIn && wasIn !== id) {
      if (!otherFamiliesTouched.has(wasIn)) otherFamiliesTouched.set(wasIn, new Set());
      otherFamiliesTouched.get(wasIn)!.add(sid);
    }
    batch.update(doc(db, COLLECTIONS.students, sid), buildMemberPatch(stu));
  }

  // 3) drop moved students from their old families' studentIds mirror
  for (const [famId, dropping] of otherFamiliesTouched) {
    const fam = families.find((f) => f.id === famId);
    if (!fam) continue;
    batch.update(doc(db, COLLECTIONS.families, famId), {
      studentIds: fam.studentIds.filter((x) => !dropping.has(x)),
      updatedAt: ts,
    });
  }

  // 4) the family doc itself
  const payload = stripUndefined({
    name: input.name.trim(),
    studentIds: memberIds,
    primaryContact: stripUndefined({ ...input.primaryContact }),
    billingAddress: input.billingAddress?.trim() || undefined,
    notes: input.notes?.trim() || undefined,
    billingPlan: input.billingPlan && input.billingPlan.kind !== 'standard' ? input.billingPlan : deleteField(),
    updatedAt: ts,
    updatedBy: actor?.uid || undefined,
    ...(existing
      ? {}
      : { isDemo: false, createdAt: ts, createdBy: actor?.uid || undefined }),
  });
  batch.set(doc(db, COLLECTIONS.families, id), payload, { merge: true });

  await batch.commit();
  return id;
}

/** Delete a family and clear familyId on all its members. */
export async function deleteFamily(family: Family): Promise<void> {
  const ts = now();
  const batch = writeBatch(db);
  for (const sid of family.studentIds) {
    batch.update(doc(db, COLLECTIONS.students, sid), {
      familyId: deleteField(),
      updatedAt: ts,
    });
  }
  batch.delete(doc(db, COLLECTIONS.families, family.id));
  await batch.commit();
}
